import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { BlogPostSummary } from '@/types'
import type { CategoryWithCount } from '@/lib/blog'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { getCategories, getPosts } from '@/lib/blog'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PostMeta } from '@/components/generals/post-meta'
import { SplitWords } from '@/components/generals/split-words'
import { SectionLabel } from '@/components/generals/section-label'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { ArticleIndex } from '@/components/landing/blogg/article-index'

interface BlogProps {
    posts: BlogPostSummary[]
    categories: CategoryWithCount[]
    active: string | null
    total: number
    failed: boolean
}

const FeaturePost: FC<{ post: BlogPostSummary }> = ({ post }): ReactNode => (
    <article className='group grid grid-cols-12 gap-x-6 gap-y-8'>
        <Link href={`/blogg/${post.slug}`} className='col-span-12 block lg:col-span-7' data-cursor='Les' tabIndex={-1} aria-hidden='true'>
            <div className='aspect-[16/10] overflow-hidden bg-tone-line' data-reveal='clip'>
                {post.coverImage && (
                    <img
                        src={post.coverImage}
                        alt=''
                        fetchPriority='high'
                        decoding='async'
                        className='size-full object-cover transition-transform duration-[1.2s] ease-out-quint group-hover:scale-105'
                    />
                )}
            </div>
        </Link>

        <div className='col-span-12 flex flex-col justify-end lg:col-span-5'>
            <p className='t-label text-tone-muted'>Siste nytt</p>
            <PostMeta post={post} className='mt-6' />
            <h2 className='t-h1 mt-5 balance'>
                <Link href={`/blogg/${post.slug}`} className='u-link'>
                    <span data-mdlfy-cms={`blog_posts:${post.id}:title`}>{post.title}</span>
                </Link>
            </h2>
            {post.excerpt && (
                <p className='mt-6 max-w-lg text-tone-muted t-body line-clamp-4' data-mdlfy-cms={`blog_posts:${post.id}:excerpt`}>{post.excerpt}</p>
            )}
        </div>
    </article>
)

const Blog: FC<BlogProps> = ({ posts, categories, active, total, failed }): ReactNode => {
    const activeCategory = categories.find(category => category.slug === active) ?? null
    const [lead, ...rest] = posts

    return (
        <Layout header='light'>
            <Seo
                title={activeCategory ? `${activeCategory.name} i bloggen` : 'Bloggen'}
                description="Guider, maler og kundehistorier for håndverkere: kalkulasjon, HMS, kvalitetssikring, sentral godkjenning og lønnsom drift."
            />

            <section id='bloggen' className='tone-paper relative overflow-hidden' data-header='light'>
                <div className='grid-lines' />

                <div className='wrap relative pt-[calc(var(--header-h)+5rem)] pb-14'>
                    <p className='t-label load-fade text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>
                        Bloggen · <span className='tabular'>{total}</span> artikler
                    </p>

                    <h1 className='t-mega mt-8'>
                        <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                            <SplitWords text={activeCategory ? activeCategory.name : 'Kunnskap som'} />
                        </span>
                        {!activeCategory && (
                            <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.3s' } as CSSProperties}>
                                <SplitWords text='lønner seg.' start={2} />
                            </span>
                        )}
                    </h1>

                    <p className='t-lead load-fade mt-10 max-w-xl text-tone-muted pretty' style={{ '--load-delay': '0.7s' } as CSSProperties}>
                        Guider, gratis maler og historier fra håndverkere som har fått bedre kontroll på jobben, og mer igjen for den.
                    </p>
                </div>
            </section>

            <nav aria-label='Kategorier' className='blog-filter tone-paper sticky top-[var(--header-h)] z-20 border-y border-tone-line' data-header='light'>
                <div className='wrap no-scrollbar flex gap-2 overflow-x-auto py-3' data-lenis-prevent-horizontal>
                    <Link
                        href='/blogg'
                        scroll={false}
                        aria-current={active === null ? 'page' : undefined}
                        className={cn('blog-chip', active === null && 'is-active')}
                    >
                        Alle <span className='tabular opacity-60'>{total}</span>
                    </Link>
                    {categories.map(category => (
                        <Link
                            key={category.id}
                            href={`/blogg?kategori=${category.slug}`}
                            scroll={false}
                            aria-current={active === category.slug ? 'page' : undefined}
                            className={cn('blog-chip', active === category.slug && 'is-active')}
                        >
                            <span data-mdlfy-cms={`blog_categories:${category.id}:name`}>{category.name}</span>
                            <span className='tabular opacity-60'>{category.count}</span>
                        </Link>
                    ))}
                </div>
            </nav>

            <section id='artikler' className='tone-paper relative py-16 md:py-24' data-header='light'>
                <div className='wrap'>
                    {failed && (
                        <p className='bg-butter p-6 text-night'>Artiklene kunne ikke lastes akkurat nå. Prøv å laste siden på nytt om litt.</p>
                    )}

                    {!failed && posts.length === 0 && (
                        <div className='py-16'>
                            <p className='t-h2'>Ingen artikler her ennå.</p>
                            <Link href='/blogg' className='u-link-static mt-6 inline-block font-semibold'>Se alle artikler</Link>
                        </div>
                    )}

                    {lead && <FeaturePost post={lead} />}

                    {rest.length > 0 && (
                        <div className='mt-20 md:mt-28'>
                            <div className='mb-8 flex items-end justify-between gap-6'>
                                <SectionLabel>{activeCategory ? 'Flere artikler' : 'Alle artikler'}</SectionLabel>
                                <span className='t-label tabular text-tone-muted'>{rest.length} til</span>
                            </div>
                            <ArticleIndex posts={rest} />
                        </div>
                    )}
                </div>
            </section>

            <section id='nyhetsbrev' className='tone-night grain relative overflow-hidden py-24 md:py-32' data-header='dark'>
                <div className='wrap grid grid-cols-12 items-end gap-x-6 gap-y-10'>
                    <div className='col-span-12 lg:col-span-6'>
                        <SectionLabel>Nyhetsbrev</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            <SplitWords text='Nye guider og maler, rett i innboksen.' />
                        </h2>
                    </div>
                    <div className='col-span-12 lg:col-span-5 lg:col-start-8'>
                        <NewsletterForm />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<BlogProps> = async ({ query, res }) => {
    const requested = typeof query.kategori === 'string' ? query.kategori : null

    res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300')

    try {
        const categories = await getCategories()
        const active = requested && categories.some(category => category.slug === requested) ? requested : null
        const posts = await getPosts(active)
        const total = categories.reduce((sum, category) => sum + category.count, 0)

        return { props: { posts, categories, active, total: Math.max(total, active ? 0 : posts.length), failed: false } }
    }
    catch (error) {
        console.error('Could not load the blog', error instanceof Error ? error.message : error)

        return { props: { posts: [], categories: [], active: null, total: 0, failed: true } }
    }
}

export default Blog
